import appTheme from "../theme";
import { alpha } from "@mui/material/styles";
import { common } from "@mui/material/colors";
import { red, blue, green } from "@mui/material/colors";
import {
  BookingStatus,
  RentContractStatus,
  TodoPriority,
  TodoStatus,
} from "src/common_constants/EnumsConstants/Booking";
import { VehicleStatusEnum } from "src/common_constants/EnumsConstants/CommonEnums";

declare module "@mui/material/styles" {
  interface Palette {
    bookingStatus: {
      [key in any]: string;
    };
    rentContractStatus: {
      [key in RentContractStatus]: string;
    };
    assetStatus: {
      [key in VehicleStatusEnum]: string;
    };
    chart: {
      [key in any]: any;
    };
    todoPriority: {
      [key in TodoPriority]: string;
    };
    todoStatus: {
      [key in TodoStatus]: string;
    };
  }
  interface PaletteOptions {
    bookingStatus?: {
      [key in BookingStatus]?: string;
    };
    rentContractStatus?: {
      [key in RentContractStatus]?: string;
    };
    assetStatus?: {
      [key in VehicleStatusEnum]: string;
    };
    chart: {
      [key in any]: any;
    };
    todoStatus: {
      [key in TodoStatus]: string;
    };
    todoPriority: {
      [key in TodoPriority]: string;
    };
  }
  interface TypeBackground {
    contractBg?: string;
    cardBg?: string;
    gradientBG?: string;
    newCustomerBg?: string;

    contractCustomerCardBg?: string;
    contractDetailsViewCardBg?: string;

    cancelButtonBg?: string;
  }
}

const neutral = {
  50: "#F8F9FA",
  100: "#F3F4F6",
  200: "#E6F0F9",
  300: "#D2D6DB",
  400: "#9DA4AE",
  500: "#5e6c84",
  600: "#6C737F",
  700: "#4D5761",
  800: "#2F3746",
  900: "#111927",
};

const customColors = {
  customColor1: red[500],
  customColor2: blue[500],
  customColor3: green[500],
  dividerBorder: "#FFFFFF3D", // for contract reservation divider border
  dividerBorderDark: "#2B867C63", // for price summary divider color
  dividerRentalInfo: "#2B867C4D", // color divider for rental info contract view
  dividerCustomerInfo: "#D9D9D991", // color divider for customer info contract view
  cardGrayBg: "#F3F3F3", //contract card gray bg for tabs
  newCustomerBg: "#3F8D7C1A",
  contractCustomerCardBg: "#8A9FD41F", //contract create edit page customer card bg
  contractDetailsViewCardBg: "#2B867C1A", //contract create details view card bg
  cancelButtonBg: "#8A9FD4",
};

const colorPalette = {
  primary: {
    light: "#06afe3",
    main: "#08163B", //new main color
    dark: "#08163B", //0c4a8a
    contrastText: "#fff",
    customColorText: "#2B867C",
    darkText: "#000000",
  },
  secondary: {
    light: "#ff9800",
    main: "#2B867C", //#FF5733 new secondary main
    dark: "#2B867C", //e65100
    contrastText: "#fff",
  },
  custom: customColors,
  neutral,
  text: {
    primary: neutral[900],
    secondary: neutral[500],
    disabled: alpha(neutral[900], 0.38),
  },
  action: {
    active: neutral[500],
    disabled: alpha(neutral[900], 0.38),
    disabledBackground: alpha(neutral[900], 0.12),
    focus: alpha(neutral[900], 0.16),
    hover: alpha(neutral[900], 0.04),
    selected: alpha(neutral[900], 0.12),
  },
  background: {
    default: common.white,
    paper: "#feffff",
    gradientBG:
      "linear-gradient(101deg, #08163B 0%, #0F2D49 39.4%, #1B545E 99.99%, #2B867C 100%)",
    contractBg: "#1B545E", //new
    cardBg: "#ffff", // card bg new
    newCustomerBg: "#3F8D7C1A", //New customer bg for cards
  },
  success: {
    main: "#4caf50",
    light: "#2e7d32",
    dark: "#1b5e20",
    contrastText: "#fff",
  },
  error: {
    main: "#ef5350",
    light: "#d32f2f",
    dark: "#c62828",
    contrastText: "#fff",
  },
  warning: {
    main: "#ff9800",
    light: "#ed6c02",
    dark: "#e65100",
    contrastText: "#fff",
  },
  chart: {
    violet: ["#826AF9", "#9E86FF", "#D0AEFF", "#F7D2FF"],
    blue: ["#2D99FF", "#83CFFF", "#A5F3FF", "#CCFAFF"],
    green: ["#2CD9C5", "#60F1C8", "#A4F7CC", "#C0F2DC"],
    yellow: ["#FFE700", "#FFEF5A", "#FFF7AE", "#FFF3D6"],
    red: ["#FF6C40", "#FF8F6D", "#FFBD98", "#FFF2D4"],
  },
  bookingStatus: {
    [BookingStatus.New]: "#1CB717",
    [BookingStatus.Quote]: "#FF7700",
    [BookingStatus.Reserved]: "#f57c00",
    [BookingStatus.ContractCreated]: "#8A9FD4",
    [BookingStatus.Confirm]: "#8A9FD4",
    [BookingStatus.ContractSigned]: "#1976d2",
    [BookingStatus.CheckedOut]: "#08163B",
    [BookingStatus.Completed]: "#1CB717",
    [BookingStatus.Cancelled]: "#FF002B",
    [BookingStatus.NoShow]: "#2B867C",
    [BookingStatus.PaymentPending]: "#EC052C",
    [BookingStatus.Void]: "#08163B",
  },
  rentContractStatus: {
    [RentContractStatus.New]: "#2196f3",
    [RentContractStatus.Active]: "#4caf50",
    [RentContractStatus.Completed]: "#1CB717",
    [RentContractStatus.Terminated]: "#f44336",
    [RentContractStatus.Void]: "#9e9e9e",
  },
  assetStatus: {
    [VehicleStatusEnum.Available]: "#1CB717",
    [VehicleStatusEnum.Rented]: "#FF002B",
    [VehicleStatusEnum.Reserved]: "#FF7700",
    [VehicleStatusEnum.Maintenance]: "#8A9FD4",
    [VehicleStatusEnum.Sold]: "#2B867C",
  },
  todoStatus: {
    [TodoStatus.New]: "#FF002B",
    [TodoStatus.Inprogress]: "#FF7700",
    [TodoStatus.Resolved]: "#1CB717",
  },
  todoPriority: {
    [TodoPriority.High]: "#FF002B",
    [TodoPriority.Medium]: "#FF7700",
    [TodoPriority.Low]: "#4caf50",
  },
};

var theme = appTheme(
  colorPalette.primary.main,
  colorPalette.primary.contrastText,
  colorPalette
);

const lightTheme = {
  ...theme,
  palette: {
    mode: "light",
    ...colorPalette,
  },
};
export default lightTheme;
